import { Capacitor } from '@capacitor/core'
import { useAuthContext } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { mixpanel } from '@/mixpanel'
import { PaymentStatus } from '../components/PaymentStatus'
import { TransactionStatus } from '../types/payment.types'
import { getPomTransactionStatus } from '../utils/pom.helpers'

/**
 * Public page to display the payment status
 *  - If the user is authenticated, they will be redirected to the home page
 *  - If the user is not authenticated, they will see the payment status
 * @returns
 */
export const PaymentStatusPage = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const [searchParameters] = useSearchParams()
  const navigate = useNavigate()
  const auth = useAuthContext()

  // Template variables
  // ------------------------------------------------------------------------- /
  const isAuthenticated = !!auth.data.current
  const responseCode = searchParameters.get('responseCode')
  const documentId = searchParameters.get('documentId')

  const transactionStatusFromPom = useMemo(
    () => (responseCode ? getPomTransactionStatus(responseCode) : null),
    [responseCode],
  )

  const paymentStatus = transactionStatusFromPom?.status ?? TransactionStatus.PROCESSING

  // Effects
  // ------------------------------------------------------------------------- /

  useEffect(() => {
    const transactionStatus = getPomTransactionStatus(responseCode)
    if (transactionStatus?.status === TransactionStatus.REFUSED) {
      mixpanel.track('payment_failed', {
        responseCode,
        documentId,
        platform: Capacitor.getPlatform(),
      })
    } else if (transactionStatus?.status === TransactionStatus.COMPLETED) {
      mixpanel.track('payment_success', {
        responseCode,
        documentId,
        platform: Capacitor.getPlatform(),
      })
    }
  }, [responseCode, documentId])

  useEffect(() => {
    if (isAuthenticated) {
      navigate(`/care/payment-status/${documentId}?responseCode=${responseCode}&documentId=${documentId}`)
    }
  }, [isAuthenticated, documentId, responseCode])

  // Handlers
  // ------------------------------------------------------------------------- /

  const handleCta = () => {
    mixpanel.track('payment_status_return_to_home', {
      responseCode,
      documentId,
      platform: Capacitor.getPlatform(),
    })
    navigate('/home')
  }

  // Render
  // ------------------------------------------------------------------------- /

  return (
    <ContentPageLayout noPadding>
      <StContainer>
        <StContent>
          <PaymentStatus status={paymentStatus} />
        </StContent>
        {paymentStatus !== TransactionStatus.PROCESSING && (
          <StFooter>
            <StButton variant='Primary' onClick={handleCta}>
              {t({
                id: 'paymentStatus.cta',
                message: 'Ga naar home',
              })}
            </StButton>
          </StFooter>
        )}
      </StContainer>
    </ContentPageLayout>
  )
}

const StContainer = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  grid-template-areas:
    'content'
    'button';

  max-width: 600px;
  margin: 0 auto;
`

const StContent = styled.div`
  grid-area: content;
  margin: 0 auto;
  max-width: 600px;
  width: 100%;
  ${ContentPagePaddingCSS}
`

const StFooter = styled.div`
  grid-area: button;
  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);

  ${({ theme }) => `
        padding: ${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space6};
        padding-bottom: calc(${theme.UI.Spacing.Space6}px + max(var(--safe-area-inset-bottom), 20px));
      `}
`

const StButton = styled(Button)`
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
`
